import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  BookOpen, 
  List, 
  Sparkles,
  ChevronRight,
  Clock,
  Loader2,
  Shield,
  FileText,
  Globe
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/ai-granth`;

export default function AIGranthBookPage({ user }) {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (bookId) {
      fetchBook();
    }
  }, [bookId]);

  const fetchBook = async () => {
    try {
      const response = await axios.get(`${API}/books/${bookId}`);
      setBook(response.data.book);
      setChapters(response.data.chapters || []);
    } catch (error) {
      console.error('Failed to fetch book:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDF8F3] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
      </div>
    );
  }

  if (!book) {
    return (
      <div className="min-h-screen bg-[#FDF8F3] flex items-center justify-center">
        <div className="text-center">
          <p className="text-amber-700 mb-4">Book not found</p>
          <button
            onClick={() => navigate('/ai-granth')}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg"
          >
            Back to Library
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F3]">
      {/* Header */}
      <div 
        className="relative py-8 px-4"
        style={{
          background: 'linear-gradient(135deg, #8B4513 0%, #654321 50%, #4A3728 100%)'
        }}
      >
        <div className="max-w-4xl mx-auto">
          <button 
            onClick={() => navigate('/ai-granth')}
            className="flex items-center gap-2 text-amber-200 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Library</span>
          </button>

          <div className="flex flex-col md:flex-row gap-6">
            {/* Cover */}
            <div className="w-32 h-44 bg-amber-100 rounded-xl shadow-lg flex items-center justify-center overflow-hidden flex-shrink-0">
              {book.cover_image ? (
                <img src={book.cover_image} alt={book.title} className="w-full h-full object-cover" />
              ) : (
                <span className="text-5xl">📖</span>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 text-white">
              {book.title_hindi && (
                <p 
                  className="text-2xl text-amber-200 mb-1"
                  style={{ fontFamily: 'Noto Serif Devanagari, serif' }}
                >
                  {book.title_hindi}
                </p>
              )}
              <h1 className="text-3xl font-bold mb-2">{book.title}</h1>
              {book.author && (
                <p className="text-amber-200 mb-3">by {book.author}</p>
              )}
              <p className="text-amber-100/80 text-sm leading-relaxed">
                {book.description}
              </p>

              <div className="flex flex-wrap items-center gap-4 mt-4">
                <span className="px-3 py-1 bg-white/10 rounded-full text-sm">
                  {book.total_chapters} Chapters
                </span>
                <span className="px-3 py-1 bg-white/10 rounded-full text-sm">
                  {book.total_verses} Verses
                </span>
                <span className="px-3 py-1 bg-white/10 rounded-full text-sm">
                  {book.language}
                </span>
                {book.is_ai_enabled && (
                  <span className="px-3 py-1 bg-amber-500 rounded-full text-sm flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    AI Enabled
                  </span>
                )}
              </div>

              {/* Source Citation Badge */}
              {book.source_info && book.source_info.public_domain && (
                <div className="mt-4 p-3 bg-white/10 rounded-lg border border-white/20">
                  <div className="flex items-start gap-2">
                    <Shield className="w-4 h-4 text-green-300 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-green-300 text-xs font-medium flex items-center gap-1">
                        <Globe className="w-3 h-3" /> PUBLIC DOMAIN
                      </p>
                      <p className="text-amber-100/70 text-xs mt-1">
                        {book.source_info.citation || 'Original Sanskrit text in public domain'}
                      </p>
                      {book.source_info.origin && (
                        <p className="text-amber-200/60 text-xs mt-1">
                          <FileText className="w-3 h-3 inline mr-1" />
                          Source: {book.source_info.origin}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Chapters List */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h2 className="text-xl font-bold text-amber-900 mb-6 flex items-center gap-2">
          <List className="w-6 h-6 text-amber-600" />
          Chapters
        </h2>

        <div className="space-y-3">
          {chapters.map((chapter) => (
            <Link
              key={chapter.id}
              to={`/ai-granth/book/${bookId}/chapter/${chapter.id}`}
              className="block bg-white rounded-xl p-4 border border-amber-100 hover:shadow-md hover:border-amber-200 transition-all group"
              data-testid={`chapter-${chapter.chapter_number}`}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-amber-200 rounded-xl flex items-center justify-center font-bold text-amber-800">
                  {chapter.chapter_number}
                </div>
                
                <div className="flex-1 min-w-0">
                  {chapter.title_hindi && (
                    <p 
                      className="text-amber-600 text-sm"
                      style={{ fontFamily: 'Noto Serif Devanagari, serif' }}
                    >
                      {chapter.title_hindi}
                    </p>
                  )}
                  <h3 className="font-semibold text-amber-900 group-hover:text-amber-700 transition-colors">
                    Chapter {chapter.chapter_number}: {chapter.title}
                  </h3>
                  {chapter.summary && (
                    <p className="text-sm text-amber-600 mt-1 line-clamp-2">
                      {chapter.summary}
                    </p>
                  )}
                  <span className="text-xs text-amber-500 mt-1 inline-block">
                    {chapter.total_verses} verses
                  </span>
                </div>

                <ChevronRight className="w-5 h-5 text-amber-400 group-hover:text-amber-600 transition-colors" />
              </div>
            </Link>
          ))}
        </div>

        {chapters.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-amber-300 mx-auto mb-4" />
            <p className="text-amber-700">No chapters available yet</p>
          </div>
        )}
      </div>
    </div>
  );
}
